import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { LogOut, Mail, Phone, Calendar, Package, Clock, Trash2, MessageCircle, FileText, Image, Download, AlertTriangle, X, Upload, Camera, User, Edit2, Star, Edit, AlertCircle, CreditCard, CheckCircle, Compass, MapPin, Users, Shield } from 'lucide-react';
import NotificationBell from '../components/NotificationBell';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import MobileBottomNav from '../components/MobileBottomNav';
import axios from 'axios';
import '../styles/DashboardPage.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getFileUrl = (fileRef) => {
  if (!fileRef) return '';
  if (fileRef.startsWith('http://') || fileRef.startsWith('https://')) return fileRef;
  return `${BACKEND_URL}/api/uploads/${fileRef}`;
};

// Avatar options
const AVATAR_OPTIONS = [
  { id: 'avatar1', color: '#5EFCE8', bg: 'linear-gradient(135deg, #5EFCE8 0%, #00B4D8 100%)' },
  { id: 'avatar2', color: '#FF6B6B', bg: 'linear-gradient(135deg, #FF6B6B 0%, #FF8E8E 100%)' },
  { id: 'avatar3', color: '#4ECDC4', bg: 'linear-gradient(135deg, #4ECDC4 0%, #44A08D 100%)' },
  { id: 'avatar4', color: '#A855F7', bg: 'linear-gradient(135deg, #A855F7 0%, #6366F1 100%)' },
  { id: 'avatar5', color: '#F59E0B', bg: 'linear-gradient(135deg, #F59E0B 0%, #EF4444 100%)' },
  { id: 'avatar6', color: '#10B981', bg: 'linear-gradient(135deg, #10B981 0%, #059669 100%)' },
];

const DashboardPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, token, logout, isAuthenticated, updateUser } = useAuth();
  const { t } = useLanguage();
  const { theme } = useTheme();
  const { isSubscribed, permission, subscribe } = usePushNotifications(user?.email);
  const currentLogo = theme === 'light' 
    ? "https://customer-assets.emergentagent.com/job_9d222022-9462-4aac-aca8-59da15d4f381/artifacts/tueqptuk_registan%20logo.png"
    : "https://customer-assets.emergentagent.com/job_422444ca-8f0f-4ebc-8f78-bee66ce7f723/artifacts/qo6wuzuf_REGISTAN%20LOGO.png";
  const [bookings, setBookings] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [tourBookings, setTourBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'bookings');
  const [deleteModal, setDeleteModal] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [selectedAvatar, setSelectedAvatar] = useState(user?.avatar || null);
  const photoInputRef = useRef(null);
  
  // Document re-upload state
  const [reuploadModal, setReuploadModal] = useState(null);
  const [reuploadFile, setReuploadFile] = useState(null);
  const [reuploadPreview, setReuploadPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const reuploadInputRef = useRef(null);
  
  // Review edit state
  const [editingReview, setEditingReview] = useState(null);
  const [editRating, setEditRating] = useState(0);
  const [editText, setEditText] = useState('');

  const loadUserData = useCallback(async () => {
    if (!token) return;

    try {
      // Fetch user's bookings
      const response = await axios.get(`${BACKEND_URL}/api/bookings/my-bookings`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      setBookings(response.data);
      
      // Fetch user's reviews
      const reviewsResponse = await axios.get(`${BACKEND_URL}/api/reviews/my-reviews`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setReviews(reviewsResponse.data);
      
      // Fetch user's invoices
      const invoicesResponse = await axios.get(`${BACKEND_URL}/api/user/invoices`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setInvoices(invoicesResponse.data);
      
      // Fetch user's tour bookings
      try {
        const tourResponse = await axios.get(`${BACKEND_URL}/api/tour-bookings/my-bookings`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setTourBookings(tourResponse.data);
      } catch (e) {
        console.error('Error loading tour bookings:', e);
      }
    } catch (error) {
      console.error('Error loading dashboard:', error);
      if (error.response?.status === 401) {
        logout();
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  }, [token, logout, navigate]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    
    loadUserData();
  }, [isAuthenticated, navigate, loadUserData]);

  // Auto-prompt push notification permission on dashboard load
  useEffect(() => {
    if (isAuthenticated && !isSubscribed && permission === 'default' && user?.email) {
      const timer = setTimeout(() => subscribe(), 3000);
      return () => clearTimeout(timer);
    }
  }, [isAuthenticated, isSubscribed, permission, subscribe, user?.email]);

  // Handle URL tab parameter
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam) {
      setActiveTab(tabParam);
      setTimeout(() => {
        let section;
        if (tabParam === 'approved') {
          section = document.querySelector('.approved-section');
        } else if (tabParam === 'transactions') {
          section = document.querySelector('.invoices-section');
        } else if (tabParam === 'profile') {
          section = document.querySelector('.profile-section');
        } else if (tabParam === 'bookings') {
          section = document.querySelector('.bookings-section');
        }
        if (section) {
          section.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 300);
    } else {
      setActiveTab('bookings');
    }
  }, [searchParams]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Review edit handlers
  const startEditReview = (review) => {
    setEditingReview(review);
    setEditRating(review.rating);
    setEditText(review.text);
  };

  const cancelEditReview = () => {
    setEditingReview(null);
    setEditRating(0);
    setEditText('');
  };

  const saveEditReview = async () => {
    if (!editingReview) return;
    
    try {
      await axios.put(
        `${BACKEND_URL}/api/reviews/${editingReview.id}`,
        { rating: editRating, text: editText, author: user?.name },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      // Update local state
      setReviews(prev => prev.map(r => 
        r.id === editingReview.id 
          ? { ...r, rating: editRating, text: editText }
          : r
      ));
      
      cancelEditReview();
      alert('Review updated successfully!');
    } catch (error) {
      console.error('Error updating review:', error);
      alert('Failed to update review');
    }
  };

  const deleteReview = async (reviewId) => {
    if (!window.confirm('Are you sure you want to delete this review?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/reviews/${reviewId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      setReviews(prev => prev.filter(r => r.id !== reviewId));
      alert('Review deleted successfully!');
    } catch (error) {
      console.error('Error deleting review:', error);
      alert('Failed to delete review');
    }
  };

  // Document re-upload handlers
  const openReuploadModal = (booking) => {
    setReuploadModal(booking);
    setReuploadFile(null);
    setReuploadPreview(null);
  };

  const handleReuploadFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('File size must be less than 5MB');
      return;
    }

    setReuploadFile(file);
    
    // Create preview for images
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => setReuploadPreview(e.target.result);
      reader.readAsDataURL(file);
    } else {
      setReuploadPreview(null);
    }
  };

  const submitReupload = async () => {
    if (!reuploadModal || !reuploadFile) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('document', reuploadFile);
      formData.append('document_type', reuploadModal.requiredDocument || 'passport');

      const response = await axios.post(
        `${BACKEND_URL}/api/bookings/${reuploadModal.id}/reupload-document`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.success) {
        alert(response.data.message);
        setReuploadModal(null);
        loadUserData(); // Refresh bookings
      }
    } catch (error) {
      console.error('Error re-uploading document:', error);
      alert(error.response?.data?.detail || 'Failed to re-upload document');
    } finally {
      setUploading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return '#FFA500';
      case 'confirmed': return '#5EFCE8';
      case 'completed': return '#00FF00';
      case 'cancelled': return '#FF0000';
      case 'document_required': return '#FF8C00';
      default: return '#888888';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'document_required': return 'Document Required';
      default: return status?.charAt(0).toUpperCase() + status?.slice(1);
    }
  };

  const handleDeleteClick = (booking) => {
    setDeleteModal(booking);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteModal) return;
    
    setDeleting(true);
    try {
      await axios.delete(`${BACKEND_URL}/api/bookings/${deleteModal.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      setDeleteModal(null);
      // Reload bookings
      loadUserData();
    } catch (error) {
      console.error('Error deleting booking:', error);
      alert('Failed to cancel booking. Please contact us directly.');
    } finally {
      setDeleting(false);
    }
  };

  const handleWhatsApp = (booking) => {
    const message = `Hello! I have a booking inquiry:\n\n` +
      `📋 Booking Number: ${booking.bookingNumber}\n` +
      `👤 Name: ${booking.name}\n` +
      `✈️ Service: ${booking.service}\n` +
      `💰 Price: ${booking.servicePrice}\n` +
      `📅 Travel Date: ${booking.date}\n` +
      `🌍 Nationality: ${booking.nationality}\n` +
      `📱 Mobile: ${booking.mobile}\n` +
      `📧 Email: ${booking.email || 'N/A'}\n` +
      `📊 Status: ${booking.status.toUpperCase()}\n\n` +
      `Please assist me with this booking. Thank you!`;

    const whatsappNumber = '971569130707'; // Remove + and spaces
    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodedMessage}`;
    
    window.open(whatsappUrl, '_blank');
  };

  // Handle profile photo upload
  const handleProfilePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file (JPG, PNG)');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert('File size must be less than 5MB');
      return;
    }

    setUploadingPhoto(true);

    try {
      const formData = new FormData();
      formData.append('photo', file);

      const response = await axios.post(
        `${BACKEND_URL}/api/auth/profile/photo`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.success) {
        // Update user in context
        updateUser({ ...user, photo: response.data.filename, avatar: null });
        setSelectedAvatar(null);
        alert('Photo uploaded successfully!');
        setShowPhotoModal(false);
      }
    } catch (error) {
      console.error('Error uploading photo:', error);
      alert('Failed to upload photo. Please try again.');
    } finally {
      setUploadingPhoto(false);
    }
  };

  // Handle avatar selection
  const handleAvatarSelect = async (avatarId) => {
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/auth/profile/avatar`,
        { avatar: avatarId },
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.data.success) {
        updateUser({ ...user, avatar: avatarId, photo: null });
        setSelectedAvatar(avatarId);
        setShowPhotoModal(false);
      }
    } catch (error) {
      console.error('Error setting avatar:', error);
      alert('Failed to update avatar. Please try again.');
    }
  };

  // Remove photo
  const handleRemovePhoto = async () => {
    try {
      await axios.delete(
        `${BACKEND_URL}/api/auth/profile/photo`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      updateUser({ ...user, photo: null, avatar: null });
      setSelectedAvatar(null);
      setShowPhotoModal(false);
    } catch (error) {
      console.error('Error removing photo:', error);
      alert('Failed to remove photo. Please try again.');
    }
  };

  // Get avatar background
  const getAvatarStyle = () => {
    if (user?.photo) {
      return { backgroundImage: `url(${getFileUrl(user.photo)})`, backgroundSize: 'cover' };
    }
    if (user?.avatar) {
      const avatar = AVATAR_OPTIONS.find(a => a.id === user.avatar);
      if (avatar) return { background: avatar.bg };
    }
    return { background: 'linear-gradient(135deg, #5EFCE8 0%, #00B4D8 100%)' };
  };

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="loading-spinner"></div>
        <p>{t('loadingDashboard')}</p>
      </div>
    );
  }

  return (
    <div className="dashboard-page">
      <Helmet>
        <title>My Dashboard - Registan Tourism</title>
        <link rel="canonical" href="https://registantours.com/dashboard" />
        <meta name="robots" content="noindex, follow" />
      </Helmet>
      {/* Header */}
      <header className="dashboard-header">
        <div className="dashboard-header-content">
          <div className="dashboard-logo" onClick={() => navigate('/')}>
            <img src={currentLogo} alt="Registan" className="logo-img" />
            <span>Registan Tourism</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <NotificationBell userEmail={user?.email} />
            <button className="btn-logout" onClick={handleLogout}>
              <LogOut size={20} />
              {t('logout')}
            </button>
          </div>
        </div>
      </header>

      <div className="dashboard-container">
        {/* User Profile Card */}
        <div className="profile-card">
          <div className="profile-avatar-section">
            <div 
              className="profile-avatar"
              style={getAvatarStyle()}
              onClick={() => setShowPhotoModal(true)}
            >
              {!user?.photo && user?.name?.charAt(0)?.toUpperCase()}
              <div className="avatar-edit-overlay">
                <Edit2 size={20} />
              </div>
            </div>
            <button 
              className="btn-edit-photo"
              onClick={() => setShowPhotoModal(true)}
            >
              <Camera size={16} />
              {t('editPhoto')}
            </button>
          </div>
          <div className="profile-info">
            <h2>{user?.name}</h2>
            <div className="profile-details">
              <div className="profile-detail">
                <Mail size={16} />
                <span>{user?.email}</span>
              </div>
              {user?.mobile && (
                <div className="profile-detail">
                  <Phone size={16} />
                  <span>{user?.mobile}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Desktop Navigation Tabs */}
        <div className="dashboard-tabs" data-testid="dashboard-tabs">
          <button 
            className={`dashboard-tab ${activeTab === 'bookings' || !activeTab ? 'active' : ''}`}
            onClick={() => setActiveTab('bookings')}
            data-testid="tab-bookings"
          >
            <Calendar size={18} />
            {t('myBookings') || 'My Bookings'}
          </button>
          <button 
            className={`dashboard-tab ${activeTab === 'approved' ? 'active' : ''}`}
            onClick={() => setActiveTab('approved')}
            data-testid="tab-approved"
          >
            <CheckCircle size={18} />
            Approved
          </button>
          <button 
            className={`dashboard-tab ${activeTab === 'tours' ? 'active' : ''}`}
            onClick={() => setActiveTab('tours')}
            data-testid="tab-tours"
          >
            <Compass size={18} />
            My Tours
          </button>
          <button 
            className={`dashboard-tab ${activeTab === 'transactions' ? 'active' : ''}`}
            onClick={() => setActiveTab('transactions')}
            data-testid="tab-transactions"
          >
            <CreditCard size={18} />
            Transactions
          </button>
          <button 
            className={`dashboard-tab ${activeTab === 'profile' ? 'active' : ''}`}
            onClick={() => setActiveTab('profile')}
            data-testid="tab-reviews"
          >
            <Star size={18} />
            {t('myReviews') || 'My Reviews'}
          </button>
        </div>

        {/* Show Approved Documents section when tab=approved */}
        {activeTab === 'approved' && (
          <>
            <div className="approved-section">
              <div className="section-header">
                <h2>
                  <CheckCircle size={24} />
                  Approved Documents
                </h2>
                <span className="booking-count">
                  {bookings.filter(b => b.adminDocument).length} total
                </span>
              </div>

              {bookings.filter(b => b.adminDocument).length === 0 ? (
                <div className="empty-state">
                  <CheckCircle size={48} />
                  <h3>No approved documents yet</h3>
                  <p>Documents sent by Registan Tourism will appear here.</p>
                </div>
              ) : (
                <div className="bookings-list">
                  {bookings.filter(b => b.adminDocument).map((booking) => (
                    <div key={booking.id} className="booking-card-dashboard" data-testid={`approved-card-${booking.bookingNumber}`}>
                      <div className="booking-header-dash">
                        <div>
                          <h3>{booking.service}</h3>
                          <p className="booking-number">#{booking.bookingNumber}</p>
                        </div>
                        <div 
                          className="booking-status"
                          style={{ 
                            backgroundColor: 'rgba(0, 255, 209, 0.15)',
                            color: '#5EFCE8',
                            border: '1px solid #5EFCE8'
                          }}
                        >
                          APPROVED
                        </div>
                      </div>

                      <div className="booking-details-dash">
                        {(booking.firstName || booking.name) && (
                          <div className="booking-detail-row">
                            <User size={18} />
                            <span>{booking.firstName && booking.lastName ? `${booking.firstName} ${booking.lastName}` : booking.name}</span>
                          </div>
                        )}
                        <div className="booking-detail-row">
                          <Calendar size={18} />
                          <span>{t('travelDateLabel') || 'Travel Date'}: {booking.date}</span>
                        </div>
                      </div>

                      <div className="booking-documents admin-documents" style={{ marginTop: '12px' }}>
                        <div className="documents-grid">
                          <a 
                            href={getFileUrl(booking.adminDocument)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="document-link download-link"
                            data-testid={`download-approved-${booking.bookingNumber}`}
                            style={{
                              display: 'flex', alignItems: 'center', gap: '10px',
                              padding: '14px 20px', background: 'rgba(94, 252, 232, 0.1)',
                              border: '1px solid rgba(94, 252, 232, 0.3)', borderRadius: '12px',
                              color: '#5EFCE8', textDecoration: 'none', fontWeight: '600',
                              fontSize: '15px', transition: 'all 0.2s ease', width: '100%',
                              justifyContent: 'center'
                            }}
                          >
                            <Download size={20} />
                            {t('downloadDocument') || 'Download Document'}
                          </a>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {/* Show Tour Bookings section when tab=tours */}
        {activeTab === 'tours' && (
          <>
            <div className="approved-section">
              <div className="section-header">
                <h2>
                  <Compass size={24} />
                  My Tour Bookings
                </h2>
                <span className="booking-count">
                  {tourBookings.length} total
                </span>
              </div>

              {tourBookings.length === 0 ? (
                <div className="empty-state">
                  <Compass size={48} />
                  <h3>No tour bookings yet</h3>
                  <p>Book a tour from our <a href="/tours" style={{ color: 'var(--brand-primary)' }}>Tours page</a> and it will appear here.</p>
                </div>
              ) : (
                <div className="bookings-list">
                  {tourBookings.map((booking) => (
                    <div key={booking.id} className="booking-card-dashboard" data-testid={`tour-booking-card-${booking.id}`}
                      style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
                      {booking.tour_image && (
                        <img src={booking.tour_image} alt={booking.tour_title}
                          style={{ width: '120px', height: '90px', borderRadius: '10px', objectFit: 'cover', flexShrink: 0 }} />
                      )}
                      <div style={{ flex: 1 }}>
                      <div className="booking-header-dash">
                        <div>
                          <h3 className="booking-service">{booking.tour_title}</h3>
                          <span className={`booking-status status-${booking.status}`}>
                            {booking.status === 'confirmed' ? 'Confirmed' : booking.status === 'cancelled' ? 'Cancelled' : 'Pending'}
                          </span>
                        </div>
                        <div className="booking-date-dash">
                          <Calendar size={14} />
                          {booking.booking_date || new Date(booking.created_at).toLocaleDateString()}
                        </div>
                      </div>
                      <div className="booking-details-grid">
                        <div className="booking-detail-item">
                          <span className="detail-label"><User size={14} /> Guest</span>
                          <span className="detail-value">{booking.guest_name}</span>
                        </div>
                        <div className="booking-detail-item">
                          <span className="detail-label"><Users size={14} /> People</span>
                          <span className="detail-value">{booking.number_of_people}</span>
                        </div>
                        <div className="booking-detail-item">
                          <span className="detail-label"><Phone size={14} /> Mobile</span>
                          <span className="detail-value">{booking.mobile}</span>
                        </div>
                        {booking.current_location && (
                          <div className="booking-detail-item">
                            <span className="detail-label"><MapPin size={14} /> Location</span>
                            <span className="detail-value">{booking.current_location}</span>
                          </div>
                        )}
                        {booking.total_price > 0 && (
                          <div className="booking-detail-item">
                            <span className="detail-label"><CreditCard size={14} /> Total</span>
                            <span className="detail-value" style={{ color: 'var(--brand-primary)', fontWeight: 700 }}>{booking.total_price} AED</span>
                          </div>
                        )}
                      </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}



        {/* Show Transactions/Invoices section when tab=transactions */}
        {activeTab === 'transactions' && (
          <>
            <div className="invoices-section">
              <div className="section-header">
                <h2>
                  <CreditCard size={24} />
                  My Invoices
                </h2>
                <span className="invoice-count">{invoices.length} total</span>
              </div>

              {invoices.length === 0 ? (
                <div className="empty-state invoices-empty">
                  <CreditCard size={48} />
                  <h3>No invoices yet</h3>
                  <p>Your invoices will appear here when generated.</p>
                </div>
              ) : (
                <div className="invoices-list-dashboard">
                  {invoices.map((invoice) => (
                    <div key={invoice.id} className={`invoice-card-dashboard status-${invoice.status}`}>
                      <div className="invoice-header-dash">
                        <div className="invoice-info">
                          <span className="invoice-number-dash">{invoice.invoice_number}</span>
                          <span className="invoice-date-dash">
                            {new Date(invoice.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        <span className={`invoice-status-badge ${invoice.status}`}>
                          {invoice.status === 'paid' && <CheckCircle size={14} />}
                          {invoice.status === 'pending' && <Clock size={14} />}
                          {invoice.status === 'partial' && <AlertCircle size={14} />}
                          {invoice.status.toUpperCase()}
                        </span>
                      </div>
                      
                      <div className="invoice-items-dash">
                        {invoice.items.map((item, idx) => (
                          <div key={idx} className="invoice-item-dash">
                            <span className="item-desc">{item.description}</span>
                            <span className="item-qty">x{item.quantity}</span>
                            <span className="item-price">AED {item.price}</span>
                          </div>
                        ))}
                      </div>
                      
                      <div className="invoice-payment-progress">
                        <div className="payment-bar">
                          <div 
                            className="payment-bar-fill" 
                            style={{ width: `${((invoice.paid_amount || 0) / (invoice.total || 1)) * 100}%` }}
                          />
                        </div>
                        <div className="payment-info">
                          <span>Paid: AED {(invoice.paid_amount || 0).toLocaleString()}</span>
                          <span>Remaining: AED {((invoice.total || 0) - (invoice.paid_amount || 0)).toLocaleString()}</span>
                        </div>
                      </div>
                      
                      <div className="invoice-footer-dash">
                        <div className="invoice-total-dash">
                          <strong>Total: AED {invoice.total?.toFixed(2)}</strong>
                        </div>
                        {invoice.due_date && (
                          <span className="invoice-due">Due: {invoice.due_date}</span>
                        )}
                      </div>
                      
                      {invoice.notes && (
                        <div className="invoice-notes-dash">
                          <small>{invoice.notes}</small>
                        </div>
                      )}
                      
                      <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'flex-end' }}>
                        <button
                          onClick={() => window.open(`${BACKEND_URL}/api/invoices/${invoice.id}/download`, '_blank')}
                          data-testid={`download-invoice-${invoice.invoice_number}`}
                          style={{
                            padding: '8px 16px', background: 'rgba(94, 252, 232, 0.1)',
                            border: '1px solid rgba(94, 252, 232, 0.3)', borderRadius: '8px',
                            color: '#5EFCE8', fontSize: '13px', fontWeight: '600',
                            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px'
                          }}
                        >
                          <Download size={14} />
                          Download
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}


        {/* Show Profile section (with Reviews) when tab=profile */}
        {activeTab === 'profile' && (
          <>
            <div className="profile-section">
              {/* My Reviews inside Profile */}
              <div className="reviews-section">
                <div className="section-header">
                  <h2>
                    <Star size={24} />
                    {t('myReviews') || 'My Reviews'}
                  </h2>
                  <span className="review-count">{reviews.length} {t('total') || 'total'}</span>
                </div>

                {reviews.length === 0 ? (
                  <div className="empty-state reviews-empty">
                    <MessageCircle size={48} />
                    <h3>{t('noReviewsYet') || 'No reviews yet'}</h3>
                    <p>{t('noReviewsDesc') || 'Share your experience with our services'}</p>
                    <button className="btn-primary" onClick={() => navigate('/#reviews')}>
                      {t('writeReview') || 'Write a Review'}
                    </button>
                  </div>
                ) : (
                  <div className="reviews-list-dashboard">
                    {reviews.map((review) => (
                      <div key={review.id} className="review-card-dashboard">
                        {editingReview?.id === review.id ? (
                          <div className="review-edit-form">
                            <div className="star-rating-edit">
                              {[1, 2, 3, 4, 5].map((star) => (
                                <Star
                                  key={star}
                                  size={24}
                                  fill={star <= editRating ? '#FFD700' : 'none'}
                                  stroke={star <= editRating ? '#FFD700' : '#666'}
                                  onClick={() => setEditRating(star)}
                                  style={{ cursor: 'pointer' }}
                                />
                              ))}
                            </div>
                            <textarea
                              value={editText}
                              onChange={(e) => setEditText(e.target.value)}
                              placeholder={t('reviewPlaceholder') || 'Write your review...'}
                              rows={4}
                            />
                            <div className="edit-actions">
                              <button onClick={cancelEditReview} className="btn-cancel-edit">
                                {t('cancel') || 'Cancel'}
                              </button>
                              <button onClick={saveEditReview} className="btn-save-edit">
                                {t('save') || 'Save'}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <div className="review-header-dash">
                              <div className="star-rating-display">
                                {[1, 2, 3, 4, 5].map((star) => (
                                  <Star
                                    key={star}
                                    size={16}
                                    fill={star <= review.rating ? '#FFD700' : 'none'}
                                    stroke={star <= review.rating ? '#FFD700' : '#666'}
                                  />
                                ))}
                              </div>
                              <span className="review-date-dash">
                                {new Date(review.createdAt).toLocaleDateString()}
                              </span>
                            </div>
                            <p className="review-text-dash">{review.text}</p>
                            <div className="review-actions-dash">
                              <button 
                                className="btn-edit-review-dash"
                                onClick={() => startEditReview(review)}
                              >
                                <Edit size={16} />
                                {t('edit') || 'Edit'}
                              </button>
                              <button 
                                className="btn-delete-review-dash"
                                onClick={() => deleteReview(review.id)}
                              >
                                <Trash2 size={16} />
                                {t('delete') || 'Delete'}
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {/* Show bookings section when tab=bookings or default */}
        {(activeTab === 'bookings' || !activeTab) && (
          <>
        {/* Bookings Section */}
        <div className="bookings-section">
          <div className="section-header">
            <h2>{t('myBookings')}</h2>
            <span className="booking-count">{bookings.length} {t('total')}</span>
          </div>

          {bookings.length === 0 ? (
            <div className="empty-state">
              <Package size={64} />
              <h3>{t('noBookingsYet')}</h3>
              <p>{t('noBookingsDesc')}</p>
              <button className="btn-primary" onClick={() => navigate('/')}>
                {t('browseServices')}
              </button>
            </div>
          ) : (
            <div className="bookings-list">
              {bookings.map((booking) => (
                <div key={booking.id} className={`booking-card-dashboard ${booking.status === 'document_required' ? 'doc-required' : ''}`}>
                  {/* Document Required Alert */}
                  {booking.status === 'document_required' && (
                    <div className="doc-required-alert">
                      <div className="alert-header">
                        <AlertCircle size={20} />
                        <span>{t('documentReuploadRequired')}</span>
                      </div>
                      <p className="alert-reason">
                        <strong>{t('required')}:</strong> {booking.requiredDocument === 'passport' ? t('passport') : t('photo')}
                      </p>
                      {booking.documentReason && (
                        <p className="alert-message">
                          <strong>{t('reason')}:</strong> {booking.documentReason}
                        </p>
                      )}
                      <button 
                        className="btn-reupload"
                        onClick={() => openReuploadModal(booking)}
                      >
                        <Upload size={16} />
                        {t('reupload')} {booking.requiredDocument === 'passport' ? t('passport') : t('photo')}
                      </button>
                    </div>
                  )}

                  <div className="booking-header-dash">
                    <div>
                      <h3>{booking.service}</h3>
                      <p className="booking-number">#{booking.bookingNumber}</p>
                    </div>
                    <div 
                      className="booking-status"
                      style={{ 
                        backgroundColor: `${getStatusColor(booking.status)}20`,
                        color: getStatusColor(booking.status),
                        border: `1px solid ${getStatusColor(booking.status)}`
                      }}
                    >
                      {getStatusLabel(booking.status).toUpperCase()}
                    </div>
                  </div>

                  <div className="booking-details-dash">
                    {/* Personal Information */}
                    {(booking.firstName || booking.name) && (
                      <div className="booking-detail-row">
                        <User size={18} />
                        <span>{t('nameLabel') || 'Name'}: {booking.firstName && booking.lastName ? `${booking.firstName} ${booking.lastName}` : booking.name}</span>
                      </div>
                    )}
                    {booking.nationality && (
                      <div className="booking-detail-row">
                        <span style={{ fontSize: '18px' }}>🌍</span>
                        <span>{t('nationalityLabel') || 'Nationality'}: {booking.nationality}</span>
                      </div>
                    )}
                    {booking.passportNumber && (
                      <div className="booking-detail-row">
                        <FileText size={18} />
                        <span>{t('passportNumberLabel') || 'Passport No'}: {booking.passportNumber}</span>
                      </div>
                    )}
                    <div className="booking-detail-row">
                      <Calendar size={18} />
                      <span>{t('travelDateLabel')}: {booking.date}</span>
                    </div>
                    <div className="booking-detail-row">
                      <Package size={18} />
                      <span>{t('priceLabel')}: {booking.servicePrice}</span>
                    </div>
                    <div className="booking-detail-row">
                      <Clock size={18} />
                      <span>{t('bookedLabel')}: {new Date(booking.createdAt).toLocaleDateString()}</span>
                    </div>
                  </div>

                  {/* Uploaded Documents */}
                  {(booking.passportFile || booking.photoFile) && (
                    <div className="booking-documents">
                      <h4>{t('yourDocuments')}</h4>
                      <div className="documents-grid">
                        {booking.passportFile && (
                          <a 
                            href={getFileUrl(booking.passportFile)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="document-link"
                          >
                            <FileText size={20} />
                            <span>{t('passport')}</span>
                          </a>
                        )}
                        {booking.photoFile && (
                          <a 
                            href={getFileUrl(booking.photoFile)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="document-link"
                          >
                            <Image size={20} />
                            <span>{t('photo')}</span>
                          </a>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Admin Documents - Visa/Approved Documents */}
                  {booking.adminDocument && (
                    <div className="booking-documents admin-documents">
                      <h4>📄 {t('documentFromRegistan')}</h4>
                      <div className="documents-grid">
                        <a 
                          href={getFileUrl(booking.adminDocument)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="document-link download-link"
                        >
                          <Download size={20} />
                          <span>{t('downloadDocument')}</span>
                        </a>
                      </div>
                    </div>
                  )}

                  <div className="booking-footer-dash">
                    <p className="booking-note">
                      {t('weWillContact')} <strong>{booking.mobile}</strong>
                    </p>
                    <div className="booking-actions">
                      {booking.status === 'pending' && (
                        <button 
                          className="btn-whatsapp"
                          onClick={() => handleWhatsApp(booking)}
                          title="Send to WhatsApp"
                        >
                          <MessageCircle size={18} />
                          {t('contactViaWhatsApp')}
                        </button>
                      )}
                      <button 
                        className="btn-delete"
                        onClick={() => handleDeleteClick(booking)}
                        title={t('cancel')}
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
          </>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteModal && (
        <div className="delete-modal-overlay" onClick={() => setDeleteModal(null)}>
          <div className="delete-modal" onClick={(e) => e.stopPropagation()}>
            <button className="delete-modal-close" onClick={() => setDeleteModal(null)}>
              <X size={24} />
            </button>
            
            <div className="delete-modal-icon">
              <AlertTriangle size={48} />
            </div>
            
            <h2>{t('deleteBookingTitle')}</h2>
            
            <div className="delete-modal-content">
              <p className="delete-warning">
                ⚠️ <strong>{t('deleteWarning')}</strong>
              </p>
              <p className="delete-info">
                {t('deleteInfo')}
              </p>
              <div className="delete-booking-info">
                <p><strong>#{deleteModal.bookingNumber}</strong></p>
                <p>{deleteModal.service}</p>
                <p>{t('travelDateLabel')}: {deleteModal.date}</p>
              </div>
              <p className="delete-permanent">
                {t('deletePermanent')}
              </p>
            </div>

            <div className="delete-modal-actions">
              <button 
                className="btn-cancel"
                onClick={() => setDeleteModal(null)}
                disabled={deleting}
              >
                {t('cancel')}
              </button>
              <button 
                className="btn-confirm-delete"
                onClick={handleDeleteConfirm}
                disabled={deleting}
              >
                {deleting ? t('deleting') : (
                  <>
                    <Trash2 size={18} />
                    {t('yesDeleteForever')}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Photo/Avatar Selection Modal */}
      {showPhotoModal && (
        <div className="photo-modal-overlay" onClick={() => setShowPhotoModal(false)}>
          <div className="photo-modal" onClick={(e) => e.stopPropagation()}>
            <button className="photo-modal-close" onClick={() => setShowPhotoModal(false)}>
              <X size={24} />
            </button>
            
            <div className="photo-modal-header">
              <Camera size={32} />
              <h2>{t('updateProfilePhoto')}</h2>
              <p>{t('uploadPhotoOrAvatar')}</p>
            </div>

            <div className="photo-modal-content">
              {/* Upload Photo Section */}
              <div className="photo-upload-section">
                <h3>{t('uploadPhoto')}</h3>
                <div 
                  className="photo-dropzone"
                  onClick={() => photoInputRef.current?.click()}
                >
                  <Upload size={32} />
                  <p>{t('clickToUpload')}</p>
                  <span>{t('jpgPng')}</span>
                </div>
                <input
                  ref={photoInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleProfilePhotoUpload}
                  style={{ display: 'none' }}
                />
              </div>

              {/* Divider */}
              <div className="photo-divider">
                <span>OR</span>
              </div>

              {/* Avatar Selection */}
              <div className="avatar-selection-section">
                <h3>{t('chooseAvatar')}</h3>
                <div className="avatar-grid">
                  {AVATAR_OPTIONS.map((avatar) => (
                    <button
                      key={avatar.id}
                      className={`avatar-option ${selectedAvatar === avatar.id ? 'selected' : ''}`}
                      style={{ background: avatar.bg }}
                      onClick={() => handleAvatarSelect(avatar.id)}
                    >
                      <User size={24} />
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="photo-modal-actions">
              {(user?.photo || user?.avatar) && (
                <button className="btn-remove-photo" onClick={handleRemovePhoto}>
                  <Trash2 size={16} />
                  {t('removePhoto')}
                </button>
              )}
              <button className="btn-cancel" onClick={() => setShowPhotoModal(false)}>
                {t('cancel')}
              </button>
            </div>

            {/* Loading Overlay */}
            {uploadingPhoto && (
              <div className="upload-loading-overlay">
                <div className="upload-spinner"></div>
                <p>{t('uploading')}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Document Re-upload Modal */}
      {reuploadModal && (
        <div className="reupload-modal-overlay" onClick={() => setReuploadModal(null)}>
          <div className="reupload-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setReuploadModal(null)}>
              <X size={24} />
            </button>
            
            <div className="reupload-modal-header">
              <AlertCircle size={32} className="reupload-icon" />
              <h2>{t('reupload')} {reuploadModal.requiredDocument === 'passport' ? t('passport') : t('photo')}</h2>
              <p>#{reuploadModal.bookingNumber}</p>
            </div>

            {reuploadModal.documentReason && (
              <div className="reupload-reason">
                <strong>{t('reason')}:</strong> {reuploadModal.documentReason}
              </div>
            )}

            <div className="reupload-content">
              <div 
                className="reupload-dropzone"
                onClick={() => reuploadInputRef.current?.click()}
              >
                {reuploadPreview ? (
                  <img src={reuploadPreview} alt="Preview" className="reupload-preview" />
                ) : (
                  <>
                    {reuploadModal.requiredDocument === 'passport' ? (
                      <FileText size={48} />
                    ) : (
                      <Image size={48} />
                    )}
                    <p>{t('clickToUpload')} {reuploadModal.requiredDocument === 'passport' ? t('passport').toLowerCase() : t('photo').toLowerCase()}</p>
                    <span>{t('jpgPngPdf')}</span>
                  </>
                )}
              </div>
              <input
                ref={reuploadInputRef}
                type="file"
                accept={reuploadModal.requiredDocument === 'photo' ? 'image/*' : 'image/*,.pdf'}
                onChange={handleReuploadFileSelect}
                style={{ display: 'none' }}
              />
              
              {reuploadFile && (
                <p className="selected-file">{t('selected')}: {reuploadFile.name}</p>
              )}
            </div>

            <div className="reupload-modal-actions">
              <button className="btn-cancel" onClick={() => setReuploadModal(null)}>
                {t('cancel')}
              </button>
              <button 
                className="btn-primary"
                onClick={submitReupload}
                disabled={!reuploadFile || uploading}
              >
                {uploading ? t('uploading') : (
                  <>
                    <Upload size={18} />
                    {t('submitDocument')}
                  </>
                )}
              </button>
            </div>

            {uploading && (
              <div className="upload-loading-overlay">
                <div className="upload-spinner"></div>
                <p>{t('uploadingDocument')}</p>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Mobile Bottom Navigation */}
      <MobileBottomNav />
      {/* Privacy link (footer-right box style) */}
      <div style={{ display: 'flex', justifyContent: 'center', padding: '24px 16px 100px' }}>
        <Link to="/privacy" className="footer-privacy-box" data-testid="footer-privacy-link">
          <Shield size={14} />
          <span>{t('privacyPolicy') || 'Privacy Policy'}</span>
        </Link>
      </div>
    </div>
  );
};

export default DashboardPage;
